import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import Hero from './Hero';
import { getInternalPath } from './ExternalLink';

// Carosello hero della home page (feature 2026-09): slide gestite dagli admin
// (badge, titolo, sottotitolo, CTA, immagine) via GET /hero-slides.
// Tre stati: `null` = in attesa della risposta → placeholder neutro con le stesse
// proporzioni del carosello (niente CLS, niente contenuto "sbagliato"); `[]` = nessuna
// slide pubblicata → vecchio hero statico; altrimenti il carosello.
// Fix 2026-09-22: prima il fallback statico copriva anche l'attesa della fetch, e nell'app
// nativa (nessun prerender) a ogni avvio si vedeva per un attimo la vecchia hero che poi
// veniva rimpiazzata dal carosello. Ora l'ultima risposta viene salvata in localStorage
// e riusata come stato iniziale, così dal secondo avvio il carosello compare subito con
// i contenuti corretti mentre la fetch li rinfresca in background.
const AUTO_ADVANCE_MS = 6000;
const SWIPE_THRESHOLD_PX = 40;
const SLIDES_CACHE_KEY = 'ticino-wild-hero-slides';

// localStorage può mancare o fallire (modalità privata, quota piena): mai bloccare il render
const readCachedSlides = () => {
    try {
        const raw = localStorage.getItem(SLIDES_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : null;
    } catch (error) {
        return null;
    }
};

const writeCachedSlides = (slides) => {
    try {
        localStorage.setItem(SLIDES_CACHE_KEY, JSON.stringify(slides));
    } catch (error) {
        // Cache best-effort: se non si può scrivere, al prossimo avvio si vedrà il placeholder
    }
};

const HeroCarousel = ({ refreshSignal = 0 }) => {
    const navigate = useNavigate();
    // Sul nativo non c'è prerender: si parte direttamente dalla cache. Sul web la home è
    // idratata dallo snapshot react-snap (che contiene il placeholder), quindi il primo
    // render deve restare `null` e la cache viene applicata subito dopo, nell'effect —
    // altrimenti hydrateRoot segnalerebbe un mismatch e ri-renderizzerebbe tutto l'albero.
    const [slides, setSlides] = useState(() => (Capacitor.isNativePlatform() ? readCachedSlides() : null));
    const [currentIndex, setCurrentIndex] = useState(0);
    const touchStartX = useRef(null);

    const fetchSlides = useCallback(async () => {
        try {
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/hero-slides`;
            const response = await fetch(url);
            if (!response.ok) return;
            const result = await response.json();
            const nextSlides = result.payload || [];
            writeCachedSlides(nextSlides);
            setSlides(nextSlides);
            setCurrentIndex(0);
        } catch (error) {
            console.error('Caricamento slide home non riuscito:', error);
        }
    }, []);

    useEffect(() => {
        if (!Capacitor.isNativePlatform()) {
            const cached = readCachedSlides();
            if (cached) setSlides(prev => prev ?? cached);
        }
    }, []);

    useEffect(() => {
        fetchSlides();
    }, [fetchSlides, refreshSignal]);

    // Carosello "infinito": il binario renderizza [clone ultima, ...slide, clone prima]
    // e l'indice vive nell'intervallo esteso -1..count. Quando l'animazione atterra su
    // un clone, handleTransitionEnd salta (senza transizione) alla slide vera equivalente:
    // così dall'ultima si continua ad avanzare verso destra ricominciando dalla prima,
    // senza il riavvolgimento veloce all'indietro.
    const [isSnapping, setIsSnapping] = useState(false);
    const count = slides ? slides.length : 0;
    const goTo = useCallback((index) => {
        if (count === 0) return;
        // Oltre i cloni non si va: gli input extra durante lo snap vengono ignorati
        if (index < -1 || index > count) return;
        setCurrentIndex(index);
    }, [count]);

    const snapFromClone = useCallback(() => {
        if (currentIndex === count) {
            setIsSnapping(true);
            setCurrentIndex(0);
        } else if (currentIndex === -1) {
            setIsSnapping(true);
            setCurrentIndex(count - 1);
        }
    }, [currentIndex, count]);

    const handleTransitionEnd = (event) => {
        if (event.target !== event.currentTarget || event.propertyName !== 'transform') return;
        snapFromClone();
    };

    // Rete di sicurezza: se transitionend non arriva (es. tab in background,
    // transizione saltata dal browser) lo snap dal clone avviene comunque
    useEffect(() => {
        if (currentIndex !== count && currentIndex !== -1) return;
        const timer = setTimeout(snapFromClone, 600);
        return () => clearTimeout(timer);
    }, [currentIndex, count, snapFromClone]);

    // Riattiva la transizione solo dopo che il salto istantaneo è stato dipinto (doppio rAF)
    useEffect(() => {
        if (!isSnapping) return;
        let raf2;
        const raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => setIsSnapping(false));
        });
        return () => {
            cancelAnimationFrame(raf1);
            if (raf2) cancelAnimationFrame(raf2);
        };
    }, [isSnapping]);

    // Auto-avanzamento (il timer riparte a ogni cambio slide, anche manuale)
    useEffect(() => {
        if (count < 2) return;
        const timer = setInterval(() => goTo(currentIndex + 1), AUTO_ADVANCE_MS);
        return () => clearInterval(timer);
    }, [count, currentIndex, goTo]);

    // In attesa della prima risposta (e senza cache): placeholder neutro con le stesse
    // dimensioni del box del carosello, così il layout non salta e non compare contenuto
    // che poi viene sostituito. È anche ciò che finisce nello snapshot react-snap
    // (la fetch verso l'API è di terze parti e viene saltata dal prerender).
    if (!slides) {
        return (
            <div className="w-full lg:w-[1000px] mt-8">
                <div className="w-full aspect-[3/2] md:aspect-auto md:h-[500px] rounded-xl bg-neutral-200 animate-pulse"></div>
            </div>
        );
    }

    // Fallback: hero statico solo quando il server dice che non ci sono slide pubblicate
    if (slides.length === 0) return <Hero />;

    // CTA: link interni in navigazione SPA, esterni nel browser (in-app su nativo) —
    // stessa politica di ExternalLink.
    const openSlideLink = (slide) => {
        if (!slide.link) return;
        const internalPath = getInternalPath(slide.link);
        if (internalPath) {
            navigate(internalPath);
        } else if (Capacitor.isNativePlatform()) {
            Browser.open({ url: slide.link });
        } else {
            window.open(slide.link, '_blank', 'noopener,noreferrer');
        }
    };

    // Con più slide il binario include i due cloni agli estremi (offset +1);
    // l'indicatore in basso mostra sempre l'indice della slide reale.
    const renderSlides = count > 1 ? [slides[count - 1], ...slides, slides[0]] : slides;
    const trackOffset = count > 1 ? currentIndex + 1 : currentIndex;
    const displayIndex = count > 0 ? ((currentIndex % count) + count) % count : 0;

    const onTouchStart = (event) => {
        touchStartX.current = event.touches[0].clientX;
    };
    const onTouchEnd = (event) => {
        if (touchStartX.current == null) return;
        const deltaX = event.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX) return;
        goTo(deltaX < 0 ? currentIndex + 1 : currentIndex - 1);
    };

    return (
        <div className="w-full lg:w-[1000px] mt-8 flex flex-col gap-3">
            {/* Wrapper relative senza overflow: le frecce vivono qui, fuori dal box
                con overflow-hidden, così possono sporgere a cavallo dei bordi */}
            <div className="relative w-full">
            {/* Su mobile la hero è orizzontale (3:2, mockup cliente 2026-09-07): l'altezza segue
                la larghezza; da md in su altezza fissa come prima */}
            <div
                className="relative w-full aspect-[3/2] md:aspect-auto md:h-[500px] rounded-xl overflow-hidden"
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
            >
                {/* Binario delle slide */}
                <div
                    className={`h-full flex ${isSnapping ? '' : 'transition-transform duration-500 ease-out'}`}
                    style={{ transform: `translateX(-${trackOffset * 100}%)` }}
                    onTransitionEnd={handleTransitionEnd}
                >
                    {
                        renderSlides.map((slide, position) => (
                            <div
                                key={`${slide._id}-${position}`}
                                className="relative h-full w-full flex-shrink-0 bg-cover bg-center bg-neutral-200"
                                style={{ backgroundImage: `url(${slide.image?.secure_url || slide.image?.resizedUrl || ''})` }}
                            >
                                {/* bg-neutral-200 sulla slide: mentre la foto scarica non si vede il bianco sotto il velo */}
                                {/* Velo per la leggibilità dei testi; con darkenImage l'admin aggiunge
                                    un oscuramento uniforme extra (foto troppo chiare) */}
                                {/* Niente rounded qui: gli angoli li arrotonda solo il box esterno
                                    (overflow-hidden), altrimenti scorrendo si vedono gli spigoli
                                    arrotondati tra una slide e l'altra */}
                                <div className={`absolute inset-0 bg-gradient-to-b from-black/50 via-black/10 to-black/50 ${slide.darkenImage ? 'bg-black/30' : ''}`}></div>
                                {/* Testi staccati dalle frecce laterali (a cavallo dei bordi, sporgono
                                    per metà dentro il box): padding orizzontale simmetrico così il copy
                                    non finisce mai sotto le frecce, né a sinistra né a destra */}
                                {/* Copy su tre zone verticali (richiesta cliente 2026-09-07): badge in alto,
                                    titolo+sottotitolo poco sotto il badge (mt fisso + mb-auto: non centrati,
                                    più vicini al badge come da mockup), bottone in basso.
                                    Su mobile padding sinistro/destro (px-9 = 36px: 8px di aria oltre i 28px di freccia interna) così i 4 elementi partono
                                    più a sinistra; da md in su spaziatura invariata */}
                                <div className="absolute inset-0 py-4 md:py-8 px-9 md:px-16 flex flex-col items-start">
                                    {
                                        slide.badge &&
                                        // Stesse dimensioni del badge "Esclusiva TicinoWild" delle card
                                        // (ActivityCard: text-xs py-0.5 px-1.5 — richiesta cliente 2026-09-07)
                                        <div className="bg-[#225424]/60 text-white text-xs py-0.5 px-1.5 rounded-md shadow">{slide.badge}</div>
                                    }
                                    <div className="mt-7 md:mt-10 mb-auto flex flex-col items-start gap-1 text-left">
                                        <div className="text-white text-xl md:text-3xl font-bold uppercase drop-shadow">{slide.title}</div>
                                        {slide.subtitle && <div className="text-white text-sm md:text-lg drop-shadow">{slide.subtitle}</div>}
                                    </div>
                                    {
                                        slide.link && slide.buttonText &&
                                        // Bottone leggermente più piccolo (richiesta cliente 2026-09-07)
                                        <button
                                            className="border-2 border-white text-white text-sm px-4 py-1.5 rounded-full flex items-center gap-1.5 hover:bg-white/20"
                                            onClick={() => openSlideLink(slide)}
                                        >
                                            {slide.buttonText}
                                            <i className="fi fi-rr-angle-small-right flex items-center"></i>
                                        </button>
                                    }
                                </div>
                            </div>
                        ))
                    }
                </div>

            </div>

            {/* Frecce (solo con più slide): a cavallo dei bordi del box (12px fuori, 28px dentro: sta entro il padding px-4 di MainContainer su mobile, così non nasce scroll orizzontale),
                stessa misura e posizione su mobile e desktop, colore pieno (richiesta cliente 2026-09-07) */}
            {
                count > 1 &&
                <>
                    <button
                        aria-label="Slide precedente"
                        className="absolute -left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white text-[#225424] flex items-center justify-center shadow z-10"
                        onClick={() => goTo(currentIndex - 1)}
                    >
                        <i className="fi fi-rr-angle-small-left flex items-center text-xl"></i>
                    </button>
                    <button
                        aria-label="Slide successiva"
                        className="absolute -right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white text-[#225424] flex items-center justify-center shadow z-10"
                        onClick={() => goTo(currentIndex + 1)}
                    >
                        <i className="fi fi-rr-angle-small-right flex items-center text-xl"></i>
                    </button>
                </>
            }
            </div>

            {/* Pallini + contatore */}
            {
                count > 1 &&
                <div className="w-full flex items-center px-2">
                    <div className="flex-1"></div>
                    <div className="flex gap-2">
                        {
                            slides.map((slide, index) => (
                                <button
                                    key={slide._id}
                                    aria-label={`Vai alla slide ${index + 1}`}
                                    className={`w-2.5 h-2.5 rounded-full ${index === displayIndex ? 'bg-[#225424]' : 'bg-neutral-300'}`}
                                    onClick={() => goTo(index)}
                                ></button>
                            ))
                        }
                    </div>
                    <div className="flex-1 text-end text-sm text-neutral-500">{displayIndex + 1} / {count}</div>
                </div>
            }
        </div>
    );
};

export default HeroCarousel;
